import { format, toZonedTime } from 'date-fns-tz';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api/reservations/types';
import { Canvas } from '@/components/canvas';
import {
  formatNumber,
  getCarImages,
  openAddressInMaps,
  snakeToTitleCase,
} from '@/core';
import { PINKLING } from '@/style/colors';
import { Button, Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { ArrowRight } from '@/ui/icons';

type Props = { resData: Reservation | undefined };

export function CarInfoCard({ resData }: Props) {
  const { styles, theme } = useStyles(stylesheet);

  return (
    <Card>
      <View
        style={{
          borderBottomColor: theme.colors.card.default.borderColor,
          borderBottomWidth: 1,
        }}
      >
        <View style={{ padding: 16 }}>
          <Text type="title2">Car Information</Text>
        </View>
      </View>

      <View style={{ padding: 16, gap: 8 }}>
        <Image
          source={{
            uri:
              resData?.car && getCarImages(resData.car.images).length > 0
                ? getCarImages(resData.car.images, 'small')[0].url
                : '',
          }}
          style={{ alignSelf: 'center', width: '70%', height: 160 }}
          contentFit="contain"
        />

        <View style={styles.carInfoGrid}>
          <View style={styles.carInfoItem}>
            <Text type="bodyBold">Make</Text>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {snakeToTitleCase(resData?.car.brandName ?? '')}
            </Text>
          </View>

          <View style={styles.carInfoItem}>
            <Text type="bodyBold">Model</Text>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {snakeToTitleCase(resData?.car.modelName ?? '')}
            </Text>
          </View>

          <View style={styles.carInfoItem}>
            <Text type="bodyBold">Year</Text>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {resData?.car.vehicleYear}
            </Text>
          </View>

          <View style={styles.carInfoItem}>
            <Text type="bodyBold">Plate</Text>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {snakeToTitleCase(resData?.car.licensePlate ?? '')}
            </Text>
          </View>

          <View style={styles.carInfoItem}>
            <Text type="bodyBold">VIN</Text>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {resData?.car.vin}
            </Text>
          </View>
        </View>
      </View>
    </Card>
  );
}

export function RentalInfoCard({ resData }: Props) {
  const { theme } = useStyles(stylesheet);

  return (
    <Card>
      <View
        style={{
          borderBottomColor: theme.colors.card.default.borderColor,
          borderBottomWidth: 1,
        }}
      >
        <View style={{ padding: 16 }}>
          <Text type="title2">Rental Information</Text>
        </View>
      </View>

      <View style={{ padding: 16, gap: 16 }}>
        <View style={{ gap: 4 }}>
          <Text type="bodyBold">
            {resData?.customPickupLocation
              ? 'Dropoff time & location'
              : 'Pickup time & location'}
          </Text>

          <Button
            type="link"
            onPress={() =>
              openAddressInMaps(
                resData?.customPickupLocation ||
                  resData?.pickupLocation.address ||
                  ''
              )
            }
            style={{
              width: '100%',
              height: 'auto',
              paddingTop: 0,
              paddingRight: 0,
              paddingBottom: 0,
              paddingLeft: 0,
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData?.customPickupLocation || resData?.pickupLocation.address}
            </Text>
          </Button>

          <Text type="bodyBold">
            {resData?.customPickupLocation
              ? 'Dropoff date & time'
              : 'Pick up date & time'}
          </Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {resData &&
              format(
                toZonedTime(resData.startTime, resData.pickupLocation.timezone),
                'MMM dd | hh:mm a'
              )}
          </Text>
        </View>

        <View
          style={{
            height: 1,
            backgroundColor: theme.colors.card.default.borderColor,
          }}
        />

        <View style={{ gap: 4 }}>
          <Text type="bodyBold">Return time & location</Text>

          <Button
            type="link"
            onPress={() =>
              openAddressInMaps(
                resData?.customReturnLocation ||
                  resData?.returnLocation.address ||
                  ''
              )
            }
            style={{
              width: '100%',
              height: 'auto',
              paddingTop: 0,
              paddingRight: 0,
              paddingBottom: 0,
              paddingLeft: 0,
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData?.customReturnLocation || resData?.returnLocation.address}
            </Text>
          </Button>

          <Text type="bodyBold">Return date & time</Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {resData &&
              format(
                toZonedTime(resData.endTime, resData.returnLocation.timezone),
                'MMM dd | hh:mm a'
              )}
          </Text>
        </View>
      </View>
    </Card>
  );
}

export function CostSummaryCard({ resData }: Props) {
  const { theme } = useStyles(stylesheet);

  // Signing/paying charges the customer what they actually owe, so for daily-
  // cadence (EVFMC) subs base the day count on the billed reservation-rate days,
  // not the full trip span. Falls back to numDays when there are no rates.
  const billedDays =
    resData?.reservationRates.reduce((sum, rate) => sum + rate.numDays, 0) ||
    resData?.numDays ||
    1;

  return (
    <Card>
      <View
        style={{
          borderBottomColor: theme.colors.card.default.borderColor,
          borderBottomWidth: 1,
        }}
      >
        <View style={{ padding: 16 }}>
          <Text type="title2">Cost Summary</Text>
        </View>
      </View>

      <View style={{ padding: 16, gap: 16 }}>
        <View style={{ gap: 8 }}>
          {resData?.reservationRates.map((rate, index) => (
            <View key={`rate-${index}`} style={{ gap: 4 }}>
              <Text type="bodyRegular">Time charges</Text>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary, width: '33%' }}
                >{`$${formatNumber(rate.dailyRate)}`}</Text>
                <Text
                  type="bodyRegular"
                  style={{
                    color: theme.colors.text.secondary,
                    width: '33%',
                    textAlign: 'center',
                  }}
                >{`x${rate.numDays} days`}</Text>
                <Text
                  type="bodyRegular"
                  style={{
                    color: theme.colors.text.secondary,
                    width: '33%',
                    textAlign: 'right',
                  }}
                >{`$${formatNumber(rate.dailyRate * rate.numDays)}`}</Text>
              </View>
            </View>
          ))}

          <View style={{ gap: 4 }}>
            <Text type="bodyRegular">{resData?.insurance.category}</Text>
            <View
              style={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary, width: '33%' }}
              >{`$${formatNumber(
                (resData?.insuranceCost ?? 0) / billedDays
              )}/day`}</Text>
              <Text
                type="bodyRegular"
                style={{
                  color: theme.colors.text.secondary,
                  width: '33%',
                  textAlign: 'center',
                }}
              >{`x${billedDays} days`}</Text>
              <Text
                type="bodyRegular"
                style={{
                  color: theme.colors.text.secondary,
                  width: '33%',
                  textAlign: 'right',
                }}
              >{`$${formatNumber(resData?.insuranceCost ?? 0)}`}</Text>
            </View>
          </View>

          {(resData?.totalCancellationPolicyCost ?? 0) > 0 ? (
            <View
              style={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Text type="bodyRegular">
                {resData?.cancellationPolicy?.title} booking
              </Text>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >{`$${formatNumber(
                resData?.totalCancellationPolicyCost ?? 0
              )}`}</Text>
            </View>
          ) : null}

          {(resData?.extras.filter((e) => e.title !== 'Custom Delivery')
            .length ?? 0) > 0 && (
            <View style={{ gap: 4 }}>
              <Text type="bodyRegular">Extras and services</Text>
              {resData?.extras
                .filter((e) => e.title !== 'Custom Delivery')
                .map((extra, index) => (
                  <View
                    key={`extra-${index}`}
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {snakeToTitleCase(extra.title)}
                    </Text>
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >{`$${formatNumber(
                      extra.recurring
                        ? extra.amount * (resData?.numDays ?? 1)
                        : extra.amount
                    )}`}</Text>
                  </View>
                ))}
            </View>
          )}

          {(resData?.reservationRates.filter((r) => !!r.discountType).length ??
            0) > 0 &&
            resData?.reservationRates
              .filter((r) => !!r.discountType)
              .map((rate, index) => (
                <View
                  key={`rate-discount-${index}`}
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular" style={{ color: PINKLING }}>
                    {rate.discountType}
                  </Text>
                  <Text
                    type="bodyRegular"
                    style={{ color: PINKLING }}
                  >{`-$${formatNumber(rate.discountAmount)}`}</Text>
                </View>
              ))}
        </View>

        <View
          style={{
            height: 1,
            backgroundColor: theme.colors.card.default.borderColor,
          }}
        />

        <View style={{ gap: 4 }}>
          <Text type="bodyRegular">Payments Made</Text>
          {resData?.payments.map((payment, index) => (
            <View
              key={`payment-${index}`}
              style={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary, flex: 1 }}
              >
                {payment.memo}
              </Text>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >{`$${formatNumber(payment.amount)}`}</Text>
            </View>
          ))}
        </View>

        <View
          style={{
            height: 1,
            backgroundColor: theme.colors.card.default.borderColor,
          }}
        />

        {((resData?.discounts.length ?? 0) > 0 ||
          (resData?.adjustments ?? 0) > 0) && (
          <>
            <View style={{ gap: 4 }}>
              {resData?.discounts.map((discount, index) => (
                <View
                  key={`discount-${index}`}
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular" style={{ color: PINKLING }}>
                    {discount.title}
                  </Text>
                  <Text
                    type="bodyRegular"
                    style={{ color: PINKLING }}
                  >{`-$${formatNumber(discount.amount)}`}</Text>
                </View>
              ))}

              {(resData?.adjustments ?? 0) > 0 && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular" style={{ color: PINKLING }}>
                    Adjustments
                  </Text>
                  <Text
                    type="bodyRegular"
                    style={{ color: PINKLING }}
                  >{`-$${formatNumber(resData?.adjustments ?? 0)}`}</Text>
                </View>
              )}
            </View>

            <View
              style={{
                height: 1,
                backgroundColor: theme.colors.card.default.borderColor,
              }}
            />
          </>
        )}

        <View style={{ gap: 4 }}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular">Total cost</Text>
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >{`$${formatNumber(resData?.totalCost ?? 0)}`}</Text>
          </View>

          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular">Outstanding balance</Text>
            <Text type="bodyRegular">{`$${formatNumber(
              resData?.outstandingBalance ?? 0
            )}`}</Text>
          </View>
        </View>
      </View>
    </Card>
  );
}

export function SignatureCard({
  onDone,
  onClear,
  onBegin,
  onEnd,
}: {
  onDone: (sig: string) => void;
  onClear: () => void;
  onBegin?: () => void;
  onEnd?: () => void;
}) {
  const { theme } = useStyles(stylesheet);

  return (
    <Card>
      <View
        style={{
          borderBottomColor: theme.colors.card.default.borderColor,
          borderBottomWidth: 1,
        }}
      >
        <View style={{ padding: 16 }}>
          <Text type="title2">Add Signature</Text>
        </View>
      </View>

      <View style={{ padding: 16, gap: 16 }}>
        <Canvas
          onDone={onDone}
          onClear={onClear}
          onBegin={onBegin}
          onEnd={onEnd}
          text="Sign above to agree with the terms and conditions"
        />

        <View
          style={{
            borderWidth: 1,
            borderColor: theme.colors.card.default.borderColor,
            borderRadius: 16,
            padding: 16,
            gap: 8,
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Text type="bodyBold">Terms and Conditions</Text>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            Please review our policies. By making a payment, you automatically
            accept our terms and conditions and our policies.
          </Text>
          <Button
            type="secondarySmall"
            text="Review Policies"
            onPress={() => router.push('/rental-terms')}
            style={{ alignSelf: 'flex-start', width: 'auto' }}
            textStyle={{ fontSize: 14 }}
            rightIcon={
              <Icon
                type="naked"
                icon={<ArrowRight />}
                fillColor="white"
                width={12}
                height={12}
              />
            }
          />
        </View>
      </View>
    </Card>
  );
}

export function TermsDivider() {
  const { theme } = useStyles(stylesheet);

  return (
    <View
      style={{
        height: 1,
        marginTop: 24,
        marginBottom: 16,
        backgroundColor: theme.colors.card.default.borderColor,
      }}
    />
  );
}

const stylesheet = createStyleSheet(() => ({
  carInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  carInfoItem: {
    gap: 4,
    minWidth: 80,
  },
}));
